import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, CheckCircle, Clock, DollarSign, Shield, ArrowRight, Download, Users, AlertCircle, Send, FileText } from 'lucide-react';

const PermitApplicationsService = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    permitType: '',
    facilityType: '',
    projectLocation: '',
    operationType: '',
    emissionSources: '',
    potentialEmissions: '',
    controlTechnology: '',
    timeline: '',
    existingPermits: '',
    previousViolations: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const permitTypes = [
    { value: '', label: 'Select permit type' },
    { value: 'title-v', label: 'Title V Operating Permit' },
    { value: 'minor-source', label: 'Minor Source Permit' },
    { value: 'psd', label: 'Prevention of Significant Deterioration (PSD)' },
    { value: 'synthetic-minor', label: 'Synthetic Minor Permit' },
    { value: 'general-permit', label: 'General Permit' },
    { value: 'nsr', label: 'New Source Review (NSR)' },
    { value: 'other', label: 'Other' }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-generation', label: 'Power Generation Facility' },
    { value: 'oil-gas', label: 'Oil & Gas Operation' },
    { value: 'military', label: 'Military Installation' },
    { value: 'seafood', label: 'Seafood Processing Plant' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'construction', label: 'Construction/Asphalt Plant' },
    { value: 'remediation', label: 'Soil Remediation Facility' },
    { value: 'geothermal', label: 'Geothermal Operation' },
    { value: 'other', label: 'Other Industrial Facility' }
  ];

  const operationTypes = [
    { value: '', label: 'Select operation type' },
    { value: 'new-construction', label: 'New Construction' },
    { value: 'modification', label: 'Facility Modification' },
    { value: 'expansion', label: 'Facility Expansion' },
    { value: 'renewal', label: 'Permit Renewal' },
    { value: 'revision', label: 'Permit Revision' }
  ];

  const timelines = [
    { value: '', label: 'Select timeline' },
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: '3-months', label: '1-3 Months' },
    { value: '6-months', label: '3-6 Months' },
    { value: '12-months', label: '6-12 Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const requiredDocuments = [
    'Project Description and Technical Specifications',
    'Facility Plot Plan and Site Layout',
    'Process Flow Diagrams',
    'Emission Calculations and Estimates',
    'Air Quality Modeling (if required)',
    'Best Available Control Technology (BACT) Analysis',
    'Compliance Demonstration Documentation',
    'Monitoring and Recordkeeping Plans',
    'Emergency Response Plans',
    'Previous Permits and Compliance History'
  ];

  const applicationSteps = [
    {
      step: 'Initial Consultation',
      description: 'We assess your project needs, determine applicable regulations, and identify required permits.',
      duration: '1-2 weeks'
    },
    {
      step: 'Information Gathering',
      description: 'Collection of all technical data, facility specifications, and emission information.',
      duration: '2-4 weeks'
    },
    {
      step: 'Application Preparation',
      description: 'Comprehensive preparation of permit application package with all required documentation.',
      duration: '4-8 weeks'
    },
    {
      step: 'Technical Review',
      description: 'Internal review of application for completeness and accuracy before submission.',
      duration: '1-2 weeks'
    },
    {
      step: 'Agency Submission',
      description: 'Formal submission to Alaska DEC or EPA with all supporting documents.',
      duration: '1 week'
    },
    {
      step: 'Agency Review & Response',
      description: 'Responding to agency comments, providing additional information as requested.',
      duration: 'Varies (2-6 months)'
    },
    {
      step: 'Permit Issuance',
      description: 'Final permit approval and issuance with conditions and requirements.',
      duration: '1-2 weeks'
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/permit-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your permit application inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          permitType: '', facilityType: '', projectLocation: '', operationType: '',
          emissionSources: '', potentialEmissions: '', controlTechnology: '',
          existingPermits: '', previousViolations: '', timeline: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Banner Section */}
      <section className="relative text-white py-20 md:py-32 overflow-hidden">
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          <source 
            srcSet="/images/hero/desktop_2560x1200.webp" 
            media="(min-width: 1440px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/tablet_1536x900.webp" 
            media="(min-width: 768px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/mobile_horizontal_1200x960.webp" 
            media="(max-width: 767px) and (orientation: landscape)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/mobile_960x1200.webp" 
            media="(max-width: 767px) and (orientation: portrait)" 
            type="image/webp"
          />
          <img 
            src="/images/hero/desktop_2560x1200.webp"
            alt="Autumn birch forest hero banner for Raven Environmental permit application services."
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </picture>
        
        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-left mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9)' }}>
              Permit Applications & Environmental Compliance
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-50 leading-relaxed drop-shadow-lg" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Expert support navigating state and federal permitting requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/permit-applications"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
              >
                Get Started
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <a
                href="#cost-savings"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-700 transition-all shadow-lg"
              >
                View Cost Savings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Alaska Air Permit Specialists Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Alaska Air Permit Specialists
            </h2>
            <p className="text-xl text-gray-600">
              Navigating Alaska's specific air quality regulations requires local expertise. We specialize in Title V, Minor Source, and General Permits, ensuring your facility is permitted correctly without over-regulation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl">
              <FileCheck className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Title V & Major Sources</h3>
              <p className="text-gray-600">
                Comprehensive management of Title V (Part 70) Operating Permits and New Source Review (NSR/PSD) applications. We manage the entire lifecycle—from emission unit inventory to negotiating permit terms that protect your operational flexibility.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl">
              <CheckCircle className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Minor Source Permitting</h3>
              <p className="text-gray-600">
                Expedited preparation of Minor Source Air Quality Control Permits under 18 AAC 50. We ensure applications meet ADEC completeness criteria on the first submittal, accelerating your construction timeline.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl">
              <Shield className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Synthetic Minor Limits</h3>
              <p className="text-gray-600">
                Strategic development of Owner Requested Limits (ORLs) to cap Potential to Emit (PTE). We help facilities legally avoid complex Title V classification while ensuring the limits leave enough headroom for actual operations.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl">
              <Clock className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">General Permits (MG)</h3>
              <p className="text-gray-600">
                Rapid authorization for ADEC General Permits. We specialize in MG1/MG2 for portable oil and gas operations and MG3/MG9 for rock crushers and asphalt plants, ensuring quick mobilization for seasonal projects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Permit Types Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Permit Types We Handle
              </h2>
              <div className="space-y-4">
                {[
                  { name: 'Air Quality Permits', desc: 'Title V operating permits, minor source permits, and PSD permits' },
                  { name: 'Construction Permits', desc: 'Pre-construction permits for new sources and modifications' },
                  { name: 'Operating Permits', desc: 'Facility-wide operating permits and renewals' },
                  { name: 'Emergency Permits', desc: 'Expedited permitting for emergency situations' },
                  { name: 'Permit Modifications', desc: 'Minor and significant permit modifications' }
                ].map((permit, index) => (
                  <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-emerald-600 mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{permit.name}</h4>
                      <p className="text-sm text-gray-600">{permit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Process</h3>
              <ol className="space-y-4">
                {[
                  { step: '1', title: 'Initial Assessment', desc: 'Review your facility, operations, and permit needs' },
                  { step: '2', title: 'Document Preparation', desc: 'Gather technical data and prepare comprehensive application' },
                  { step: '3', title: 'Regulatory Review', desc: 'Ensure compliance with all applicable requirements' },
                  { step: '4', title: 'Submission', desc: 'Submit complete application to appropriate agencies' },
                  { step: '5', title: 'Follow-Up', desc: 'Respond to agency questions and provide additional information' },
                  { step: '6', title: 'Final Approval', desc: 'Obtain permit and implement compliance requirements' }
                ].map((item) => (
                  <li key={item.step} className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                      {item.step}
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Savings Section */}
      <section id="cost-savings" className="py-16 bg-gradient-to-br from-emerald-900 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold mb-4">Cost-Saving Strategies</h2>
            <p className="text-xl text-emerald-100">
              Proven techniques that reduce your total permitting costs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <div className="text-4xl font-bold text-amber-400 mb-2">30-40%</div>
              <h3 className="text-xl font-semibold mb-3">Time Reduction</h3>
              <p className="text-emerald-100">
                Reduce permit processing time through optimized application preparation and established regulatory relationships.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <div className="text-4xl font-bold text-amber-400 mb-2">$10K-40K</div>
              <h3 className="text-xl font-semibold mb-3">Typical Savings</h3>
              <p className="text-emerald-100">
                Average cost savings per permit through efficient preparation, avoiding resubmissions, and right-sized engineering.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <div className="text-4xl font-bold text-amber-400 mb-2">20-35%</div>
              <h3 className="text-xl font-semibold mb-3">Cost Reduction</h3>
              <p className="text-emerald-100">
                Average percentage reduction in total project costs compared to traditional over-engineered approaches.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-white/10 backdrop-blur-sm p-8 rounded-xl">
            <h3 className="text-2xl font-bold mb-4">How We Reduce Your Costs</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Comprehensive initial assessment eliminates redundant work',
                'Streamlined data collection saves engineering time',
                'Pre-submission regulatory consultation prevents rejections',
                'Efficient documentation reduces consultant hours',
                'Proactive compliance prevents costly violations',
                'Long-term relationship building with agencies'
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-emerald-100">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Helpful Resources
            </h2>
            <p className="text-xl text-gray-600">
              Free guides and information to help you understand the permit process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Alaska DEC Air Quality Permits', 
                desc: 'Official Alaska Department of Environmental Conservation air quality permitting information and requirements',
                link: 'https://dec.alaska.gov/air/air-permit/'
              },
              { 
                title: 'EPA Title V Overview', 
                desc: 'Federal operating permit program requirements and guidelines',
                link: 'https://www.epa.gov/title-v-operating-permits'
              },
              { 
                title: 'Permit Application Checklist', 
                desc: 'Essential documents and information needed for permit applications',
                link: 'https://dec.alaska.gov/media/8399/title-v-application-checklist.pdf'
              }
            ].map((resource, index) => (
              <a 
                key={index}
                href={resource.link}
                target={resource.link.startsWith('http') ? '_blank' : '_self'}
                rel={resource.link.startsWith('http') ? 'noopener noreferrer' : ''}
                className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-600"
              >
                <Download className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 mb-4">{resource.desc}</p>
                <span className="text-blue-600 font-semibold hover:underline">
                  Learn More →
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-left">The Permit Application Process</h2>
          <div className="max-w-5xl mx-auto space-y-6">
            {applicationSteps.map((step, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-emerald-700">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-700 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.step}</h3>
                    <p className="text-gray-600 mb-2">{step.description}</p>
                    <p className="text-sm text-emerald-700 font-semibold">Typical Duration: {step.duration}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documentation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-left">Required Documentation</h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
              <div className="flex items-start">
                <AlertCircle className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <strong>Important:</strong> The specific documentation requirements vary based on your facility type, 
                  permit type, and project scope. We will work with you to identify all applicable requirements.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requiredDocuments.map((doc, index) => (
                <div key={index} className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <Download className="w-5 h-5 text-emerald-700 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Types of Permits We Prepare */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-left">Types of Permits We Prepare</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Title V Operating Permits</h3>
              <p className="text-gray-600">
                Major source operating permits for facilities with potential emissions above federal thresholds. 
                Comprehensive permits covering all emission sources and regulatory requirements.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Minor Source Permits</h3>
              <p className="text-gray-600">
                State air quality permits for facilities below major source thresholds. Streamlined process with 
                Alaska-specific requirements and compliance obligations.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">PSD Permits</h3>
              <p className="text-gray-600">
                Prevention of Significant Deterioration permits for new major sources or major modifications. 
                Includes BACT analysis and air quality impact modeling.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">General Permits</h3>
              <p className="text-gray-600">
                Pre-approved permits for common source categories. Simplified application process for 
                qualifying facilities with standard operating parameters.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Synthetic Minor Permits</h3>
              <p className="text-gray-600">
                Permits with enforceable limits to remain below major source thresholds. Cost-effective 
                alternative avoiding Title V requirements.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Permit Modifications</h3>
              <p className="text-gray-600">
                Administrative, minor, and significant modifications to existing permits. Expert navigation 
                of modification procedures and timelines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Inquiry Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-emerald-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Start Your Permit Application</h2>
                  <p className="text-emerald-100 mt-2">Provide details about your project for a customized consultation</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Project Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permit Type Needed
                    </label>
                    <select
                      name="permitType"
                      value={formData.permitType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {permitTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facility Type
                    </label>
                    <select
                      name="facilityType"
                      value={formData.facilityType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {facilityTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Location
                    </label>
                    <input
                      type="text"
                      name="projectLocation"
                      value={formData.projectLocation}
                      onChange={handleChange}
                      placeholder="City/Region in Alaska"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operation Type
                    </label>
                    <select
                      name="operationType"
                      value={formData.operationType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {operationTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Details</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emission Sources
                  </label>
                  <textarea
                    name="emissionSources"
                    value={formData.emissionSources}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe emission sources (e.g., engines, boilers, process equipment)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Potential Emissions
                  </label>
                  <input
                    type="text"
                    name="potentialEmissions"
                    value={formData.potentialEmissions}
                    onChange={handleChange}
                    placeholder="e.g., NOx: 10 tons/year, PM: 5 tons/year"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Planned Control Technology
                  </label>
                  <textarea
                    name="controlTechnology"
                    value={formData.controlTechnology}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe emission control equipment or measures"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Compliance History */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Compliance History</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Permits
                    </label>
                    <select
                      name="existingPermits"
                      value={formData.existingPermits}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes - Have existing permits</option>
                      <option value="no">No - New facility</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Previous Violations
                    </label>
                    <select
                      name="previousViolations"
                      value={formData.previousViolations}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      <option value="no">No violations</option>
                      <option value="yes">Yes - violations on record</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Timeline and Additional Info */}
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {timelines.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., BACT requirements, modeling needs, timeline constraints, compliance concerns, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any additional details about your project, special requirements, or questions"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-colors shadow-lg"
                >
                  Submit Application Inquiry
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Permit Application?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get expert guidance from Alaska's leading environmental consultants
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/permit-applications"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
            >
              <Users className="mr-2" size={20} />
              Schedule Consultation
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Learn About Raven
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PermitApplicationsService;
